'use client';

import { Suspense } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MainNavbar } from '@/components/layout/MainNavbar';
import { Footer } from '@/components/Footer';
import { useSettings } from '@/contexts/SettingsContext';
import { Home, Calculator, ArrowLeft } from 'lucide-react';

function NotFoundContent() {
  const settings = useSettings();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50 flex flex-col">
      <MainNavbar 
        currency={settings.currency}
        onCurrencyChange={settings.setCurrency}
      />
      
      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl flex items-center justify-center">
        <Card className="w-full max-w-2xl shadow-xl">
          <CardContent className="p-12 text-center">
            {/* 404 Graphic */}
            <div className="mb-8">
              <div className="text-8xl font-bold text-accessnow-orange mb-4">404</div>
              <div className="w-24 h-1 bg-gradient-to-r from-accessnow-orange to-orange-600 mx-auto rounded-full"></div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-bold mb-4 text-foreground">
              Page Not Found
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Sorry, we couldn&apos;t find the page you&apos;re looking for. The page may have been moved, 
              deleted, or the URL might be incorrect.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link href="/">
                <Button size="lg" className="bg-accessnow-orange hover:bg-accessnow-orange/90 text-white shadow-lg">
                  <Home className="h-5 w-5 mr-2" />
                  Go to Homepage
                </Button>
              </Link>
              
              <Link href="/calculator">
                <Button size="lg" variant="outline" className="border-accessnow-orange text-accessnow-orange hover:bg-accessnow-orange hover:text-white">
                  <Calculator className="h-5 w-5 mr-2" />
                  ROI Calculator
                </Button>
              </Link>
            </div>

            {/* Back Button */}
            <div className="mt-8 pt-6 border-t border-border">
              <Button
                variant="ghost"
                onClick={() => window.history.back()}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>

            {/* Helpful Links */}
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                Helpful Links
              </h3>
              <div className="flex flex-wrap gap-4 justify-center text-sm">
                <Link href="/about" className="text-accessnow-orange hover:underline">
                  About AccessNow
                </Link>
                <Link href="/examples" className="text-accessnow-orange hover:underline">
                  Examples
                </Link>
                <Link href="/changelog" className="text-accessnow-orange hover:underline">
                  Changelog
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer 
        showDebugModal={false}
        showChangelog={true}
        showHomeLink={true}
      />
    </div>
  );
}

export default function NotFound() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <NotFoundContent />
    </Suspense>
  );
}